import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { ILatLng } from '../../../../shared/shared.definitions';
import { CollectionMapService } from '../../collection-map.service';
import { CollectionCardComponent } from '../collection-card/collection-card.component';
import { UntypedFormControl } from '@angular/forms';
import { CollectionFilterService } from '../../collection-filter.service';
import { takeUntil, debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-collection-map',
  templateUrl: './collection-map.component.html',
  styleUrls: ['./collection-map.component.scss'],
})
export class CollectionMapComponent implements OnChanges, OnInit, OnDestroy {
  @Input() cards: any[];
  @Input() searchControl: UntypedFormControl;
  @Input() type: string;
  @Input() uuid: string;
  @Input() componentUuid: string;

  private readonly destroy$: Subject<boolean> = new Subject<boolean>();
  isListOpen = true;
  map: google.maps.Map;
  options: google.maps.MapOptions;
  markers: google.maps.Marker[] = [];
  markers$ = new BehaviorSubject<any[]>([]);
  centerTo: ILatLng;
  searchControlValueSub: Subscription | null = null;

  constructor(public readonly collectionMapService: CollectionMapService, public readonly filterService: CollectionFilterService) {}

  ngOnChanges(): void {
    this.addMarkers();
  }

  ngOnInit(): void {
    if (!this.searchControl) return;
    this.searchControlValueSub = this.searchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value === '') {
        this.onSearch();
      }
    });

    this.filterService.filters$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
      this.addMarkers();
    });
  }

  get search$(): Observable<string> {
    return this.collectionMapService.search$;
  }

  get allItemsLength$(): Observable<number> {
    return this.collectionMapService.allItemsLength$;
  }

  get isLoaderExist$(): Observable<boolean> {
    return this.collectionMapService.isLoaderExist$;
  }

  get cardsLength$(): Observable<number> {
    return this.collectionMapService.queryLength$;
  }

  get isClearFilterActive$(): Observable<boolean> {
    return this.collectionMapService.isClearFilterActive$;
  }

  setPageCounter(): void {
    this.collectionMapService.setPageCounter();
  }

  addMarkers(): void {
    const markers: any[] = [];
    this.removeMarkers();

    if (this.cards?.length) {
      this.cards.forEach((item) => {
        const lat = item.location?.lat ?? null;
        const lng = item.location?.lng ?? null;

        if (lat && lng) {
          markers.push({
            coordinate: { lat: +lat, lng: +lng },
            component: CollectionCardComponent,
            data: {
              card: item,
              isMapView: true,
            },
          });
        }
      });
      this.markers$.next(markers);
    }
  }

  removeMarkers(): void {
    this.markers$.next([]);
  }

  toggleMap(): void {
    this.collectionMapService.toggleMap();
  }

  toggleList(): void {
    this.isListOpen = !this.isListOpen;
  }

  onCenterEventEmitter(val: ILatLng): void {
    this.centerTo = val;
  }

  onAllItemClick(): void {
    this.collectionMapService.getAllItem();
  }

  public onSearch(): void {
    this.filterService.updateFilter('search', this.searchControl.value);
  }

  clearAllFilters(): void {
    this.searchControl.setValue('');
    this.filterService.resetFilters();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
